import * as React from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import KeyIcon from "@mui/icons-material/Key";
import ListItemText from "@mui/material/ListItemText";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Collapse from "@mui/material/Collapse";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EditIcon from "@mui/icons-material/Edit";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BarChartIcon from "@mui/icons-material/BarChart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PrintIcon from "@mui/icons-material/Print";
import InventoryIcon from "@mui/icons-material/Inventory";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddTaskIcon from "@mui/icons-material/AddTask";
import DataSaverOnSharpIcon from "@mui/icons-material/DataSaverOnSharp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { Tooltip } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import OrgDetailsPopup       from "../Admin/OrgDetailsPopup";
import { handleLogout }      from "../Services/AuthService";
import { useSubscription }   from "../Hooks/useSubscription";

const DEFAULT_DRAWER_WIDTH = 240;

// ─── Styled components (unchanged) ────────────────────────────────────────────

const openedMixin = (theme: Theme, drawerWidth: number): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing:   theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing:   theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display:         "flex",
  alignItems:      "center",
  justifyContent:  "flex-end",
  padding:         theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?:        boolean;
  drawerwidth?: number;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "drawerwidth",
})<AppBarProps>(({ theme, open, drawerwidth = DEFAULT_DRAWER_WIDTH }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing:   theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerwidth,
    width:      `calc(100% - ${drawerwidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing:   theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const ResizableDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop: PropertyKey) =>
    prop !== "open" && prop !== "drawerWidth",
})<{ open: boolean; drawerWidth: number }>(({ theme, open, drawerWidth }) => ({
  width:      drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing:  "border-box",
  position:   "relative",
  ...(open && {
    ...openedMixin(theme, drawerWidth),
    "& .MuiDrawer-paper": openedMixin(theme, drawerWidth),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const ResizeHandle = styled("div")(({ theme }) => ({
  position:        "absolute",
  top:             0,
  right:           0,
  bottom:          0,
  width:           "10px",
  cursor:          "col-resize",
  zIndex:          theme.zIndex.drawer + 2,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
}));

// ── Small helper for a locked SUB-item row (still visible, grayed, lock icon,
// not clickable/navigable). Used only inside already-visible menu groups —
// never at the top-level group, which is hidden instead (see render below). ─
interface ILockableSubItemProps {
  to: string;
  label: string;
  icon: React.ReactNode;
  locked: boolean;
}
const SubMenuItem: React.FC<ILockableSubItemProps> = ({ to, label, icon, locked }) => {
  if (locked) {
    return (
      <ListItemButton sx={{ pl: 4 }} disabled>
        <Tooltip title={`${label} — upgrade your plan to unlock`} placement="right">
          <ListItemIcon>{icon}</ListItemIcon>
        </Tooltip>
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <span>{label}</span>
              <LockOutlinedIcon sx={{ fontSize: 14, opacity: 0.6 }} />
            </Box>
          }
        />
      </ListItemButton>
    );
  }
  return (
    <ListItemButton component={Link} to={to} sx={{ pl: 4 }}>
      <Tooltip title={label} placement="right">
        <ListItemIcon>{icon}</ListItemIcon>
      </Tooltip>
      <ListItemText primary={label} />
    </ListItemButton>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function MiniDrawer() {
  const orgName = localStorage.getItem("orgName");
  const theme   = useTheme();
  const navigate = useNavigate();

  // ── Single source of truth for what this org's plan (± manual overrides)
  // allows. hasFeature = MENU-level (whole group visible or not).
  // hasAction = SUB-MENU-level (one row inside a visible group locked or not). ─
  const { hasFeature, hasAction, refreshSubscription, loading: subscriptionLoading } = useSubscription();

  const [open,                  setOpen]                  = React.useState(false);
  const [showAdminSubmenu,      setShowAdminSubmenu]      = React.useState(false);
  const [showSalesSubmenu,      setShowSalesSubmenu]      = React.useState(false);
  const [showReportsSubmenu,    setShowReportsSubmenu]    = React.useState(false);
  const [showReceiptSubmenu,    setShowReceiptSubmenu]    = React.useState(false);
  const [showInventorySubmenu,  setShowInventorySubmenu]  = React.useState(false);
  const [showMasterSubmenu,     setShowMasterSubmenu]     = React.useState(false);
  const [showPurchaseSubmenu,   setShowPurchaseSubmenu]   = React.useState(false);
  const [showLedgerSubmenu,     setShowLedgerSubmenu]     = React.useState(false);
  const [drawerWidth,           setDrawerWidth]           = React.useState(DEFAULT_DRAWER_WIDTH);
  const [isPopupVisible,        setPopupVisible]          = React.useState(false);
  const [isResizing,            setIsResizing]            = React.useState(false);

  const handleDrawerOpen  = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);
  const openPopup         = () => setPopupVisible(true);
  const closePopup        = () => setPopupVisible(false);

  const toggleSubmenu = (menu: string) => {
    setShowAdminSubmenu(    menu === "admin"      ? !showAdminSubmenu     : false);
    setShowMasterSubmenu(   menu === "master"     ? !showMasterSubmenu    : false);
    setShowSalesSubmenu(    menu === "sales"      ? !showSalesSubmenu     : false);
    setShowReportsSubmenu(  menu === "reports"    ? !showReportsSubmenu   : false);
    setShowReceiptSubmenu(  menu === "receipt"    ? !showReceiptSubmenu   : false);
    setShowPurchaseSubmenu( menu === "purchase"   ? !showPurchaseSubmenu  : false);
    setShowInventorySubmenu(menu === "inventory"  ? !showInventorySubmenu : false);
    setShowLedgerSubmenu(   menu === "ledger"     ? !showLedgerSubmenu    : false);
  };

  // ── Drawer resize ─────────────────────────────────────────────────────────
  const startResizing = React.useCallback(() => setIsResizing(true),  []);
  const stopResizing  = React.useCallback(() => setIsResizing(false), []);

  const resize = React.useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        setDrawerWidth(Math.min(Math.max(e.clientX, 200), 500));
      }
    },
    [isResizing]
  );

  React.useEffect(() => {
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup",   stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup",   stopResizing);
    };
  }, [resize, stopResizing]);

  // ── MENU-level visibility. While subscription info is still loading,
  // default to visible (true) rather than hidden, so the sidebar doesn't
  // flash empty on every page load — the route guard (ProtectedFeatureRoute)
  // remains the real gate regardless of what the sidebar shows. ───────────
  const showInventoryMenu = subscriptionLoading || hasFeature("inventory");
  const showPurchaseMenu  = subscriptionLoading || hasFeature("purchaseOrder");
  const showReportsMenu   = subscriptionLoading || hasFeature("reports");
  const showLedgerMenu   = subscriptionLoading || hasFeature("ledger");
  const showReceiptMenu   = subscriptionLoading || hasFeature("receipt");

  // Master (category/brand/manufacturer/warehouse) rides on "inventory" —
  // same choice as the route wiring in App.tsx.
  const showMasterMenu    = showInventoryMenu;

  // ── SUB-MENU-level locking, only relevant while the Reports group above
  // is actually visible. Each key must match the plan's
  // features.reports.actions.<key> in subscriptionPlans (and the `action`
  // prop used on the matching <ProtectedFeatureRoute> in App.tsx). ────────
  const reportsLocked = {
    salesRegister: !hasAction("reports", "salesRegister"),
    b2bGst:        !hasAction("reports", "b2bGst"),
    salesByItem:   !hasAction("reports", "salesByItem"),
    categoryStock: !hasAction("reports", "categoryStock"),
  };
    const inventoryLocked = {
    addInventory: !hasAction("inventory", "addInventory"),
    manageStock:  !hasAction("inventory", "manageStock"),
    viewInventory:   !hasAction("inventory", "viewInventory"),
  };


  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        sx={{ backgroundColor: "#663399" }}
        position="fixed"
        open={open}
        drawerwidth={drawerWidth}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ marginRight: 5, ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {orgName}
          </Typography>

          <IconButton color="inherit" aria-label="org profile" onClick={openPopup}>
            <AccountCircleIcon />
          </IconButton>
          {isPopupVisible && <OrgDetailsPopup onClose={closePopup} />}

          <Tooltip title="Sign out">
            <IconButton
              color="inherit"
              aria-label="sign out"
              onClick={() => {
                handleLogout();
                refreshSubscription(); // clears local subscription state now that the token is gone
              }}
            >
              <ExitToAppIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <ResizableDrawer
        variant="permanent"
        open={open}
        drawerWidth={drawerWidth}
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "#f0f8ff",
            color: "#000",
          },
        }}
      >
        <ResizeHandle onMouseDown={startResizing} />
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />

        <List
          sx={{
            "& .MuiListItemText-primary": {
              fontWeight:        "bold",
              fontFamily:        '"Poppins", sans-serif',
              fontSize:          "0.9rem",
              color:             "#556b2f",
              whiteSpace:        "normal",
              overflow:          "hidden",
              textOverflow:      "ellipsis",
              display:           "-webkit-box",
              WebkitLineClamp:   2,
              WebkitBoxOrient:   "vertical",
            },
            "& .MuiListItemButton-root": { py: 1.5 },
            "& .Mui-disabled": { opacity: 0.5 },
          }}
        >
          {/* Dashboard — always available */}
          <ListItemButton component={Link} to="/dashboard">
            <Tooltip title="Dashboard" placement="right">
              <ListItemIcon><DashboardIcon /></ListItemIcon>
            </Tooltip>
            <ListItemText primary="DASHBOARD" />
          </ListItemButton>

          {/* Master — HIDDEN entirely when the org has no Inventory rights
              at all (not locked/grayed — simply not rendered). */}
          {showMasterMenu && (
            <>
              <ListItemButton onClick={() => toggleSubmenu("master")}>
                <Tooltip title="Master" placement="right">
                  <ListItemIcon><AddTaskIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="MASTER" />
                {showMasterSubmenu ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={showMasterSubmenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {[
                    { to: "Master/inventory/add-category",     label: "Category Master" },
                    { to: "Master/inventory/add-brand",        label: "Brand Master" },
                    { to: "Master/inventory/add-manufacturer", label: "Manufacturer Master" },
                    { to: "Master/inventory/add-warehouse",    label: "Warehouse Master" },
                  ].map(({ to, label }) => (
                    <ListItemButton key={to} component={Link} to={to} sx={{ pl: 4 }}>
                      <Tooltip title={label} placement="right">
                        <ListItemIcon><DataSaverOnSharpIcon /></ListItemIcon>
                      </Tooltip>
                      <ListItemText primary={label} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </>
          )}

          {/* Admin — not currently plan-gated */}
          <ListItemButton onClick={() => toggleSubmenu("admin")}>
            <Tooltip title="Admin" placement="right">
              <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
            </Tooltip>
            <ListItemText primary="ADMIN" />
            {showAdminSubmenu ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={showAdminSubmenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton component={Link} to="/dashboard/admin/edit-org" sx={{ pl: 4 }}>
                <Tooltip title="Edit Org Profile" placement="right">
                  <ListItemIcon><EditIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Edit Org Profile" />
              </ListItemButton>
              <ListItemButton component={Link} to="Admin/view-user" sx={{ pl: 4 }}>
                <Tooltip title="View Users" placement="right">
                  <ListItemIcon><VisibilityIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="View Users" />
              </ListItemButton>
              <ListItemButton component={Link} to="Admin/change-password" sx={{ pl: 4 }}>
                <Tooltip title="Change Password" placement="right">
                  <ListItemIcon><KeyIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Change Password" />
              </ListItemButton>
            </List>
          </Collapse>

          {/* Purchase — HIDDEN entirely when the plan has no Purchase Order rights */}
          {showPurchaseMenu && (
            <>
              <ListItemButton onClick={() => toggleSubmenu("purchase")}>
                <Tooltip title="Purchase" placement="right">
                  <ListItemIcon><AssignmentIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="PURCHASE" />
                {showPurchaseSubmenu ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={showPurchaseSubmenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton component={Link} to="/dashboard/purchase/purchase-order-form" sx={{ pl: 4 }}>
                    <Tooltip title="Create Purchase Order" placement="right">
                      <ListItemIcon><AddTaskIcon /></ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="Create PO" />
                  </ListItemButton>
                  <ListItemButton component={Link} to="/dashboard/purchase/view-purchase-orders" sx={{ pl: 4 }}>
                    <Tooltip title="View Purchase Order" placement="right">
                      <ListItemIcon><VisibilityIcon /></ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="View PO" />
                  </ListItemButton>
                </List>
              </Collapse>
            </>
          )}

          {/* Inventory — HIDDEN entirely when the plan has no Inventory rights */}
          {showInventoryMenu && (
            <>
              <ListItemButton onClick={() => toggleSubmenu("inventory")}>
                <Tooltip title="Inventory" placement="right">
                  <ListItemIcon><InventoryIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="INVENTORY" />
                {showInventorySubmenu ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={showInventorySubmenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {/* <ListItemButton component={Link} to="/dashboard/inventory/add-new-item" sx={{ pl: 4 }}>
                    <Tooltip title="Add Inventory" placement="right">
                      <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="Add Inventory" />
                  </ListItemButton> */}
                  <SubMenuItem
                    to="/dashboard/inventory/add-new-item"
                    label="Add Inventory"
                    icon={<AddCircleOutlineIcon />}
                    locked={inventoryLocked.addInventory}
                  />
                  {/* <ListItemButton component={Link} to="/dashboard/inventory/view-inventory" sx={{ pl: 4 }}>
                    <Tooltip title="View Inventory" placement="right">
                      <ListItemIcon><VisibilityIcon /></ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="View Inventory" />
                  </ListItemButton> */}
                    <SubMenuItem
                    to="/dashboard/inventory/view-inventory"
                    label="View Inventory"
                    icon={<VisibilityIcon />}
                    locked={inventoryLocked.viewInventory}
                  />
                  {/* <ListItemButton component={Link} to="/dashboard/inventory/stock-management" sx={{ pl: 4 }}>
                    <Tooltip title="Stock Management" placement="right">
                      <ListItemIcon><InventoryIcon /></ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="Stock Management" />
                  </ListItemButton> */}
                   <SubMenuItem
                    to="/dashboard/inventory/stock-management"
                    label="Stock Management"
                    icon={<InventoryIcon />}
                    locked={inventoryLocked.manageStock}
                  />
                </List>
              </Collapse>
            </>
          )}

          {/* Sales — always available */}
          <ListItemButton onClick={() => toggleSubmenu("sales")}>
            <Tooltip title="Sales" placement="right">
              <ListItemIcon><MonetizationOnIcon /></ListItemIcon>
            </Tooltip>
            <ListItemText primary="SALES" />
            {showSalesSubmenu ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={showSalesSubmenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {[
                { to: "/dashboard/sales/new-invoice",          label: "New Invoice",      icon: <AddCircleOutlineIcon /> },
                { to: "/dashboard/sales/rapid-Quick-invoice",  label: "GST Invoice",      icon: <AssignmentIcon /> },
                { to: "/dashboard/sales/quick-invoice",        label: "Quick Invoice",    icon: <AssignmentIcon /> },
                { to: "/dashboard/sales/performa-invoice",     label: "Performa Invoice", icon: <AssignmentIcon /> },
                { to: "/dashboard/sales/credit-note",         label: "Credit Note",       icon: <AddCircleOutlineIcon color="success" /> },
                { to: "/dashboard/sales/debit-note",         label: "Debit Note",         icon:<RemoveCircleOutlineIcon color="error" />},
                 { to: "/dashboard/sales/view-invoice",         label: "View Invoice",     icon: <VisibilityIcon /> },
              ].map(({ to, label, icon }) => (
                <ListItemButton key={to} component={Link} to={to} sx={{ pl: 4 }}>
                  <Tooltip title={label} placement="right">
                    <ListItemIcon>{icon}</ListItemIcon>
                  </Tooltip>
                  <ListItemText primary={label} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>

          {/* Ledger — not currently plan-gated */}
           {showLedgerMenu && (
            <>
          <ListItemButton onClick={() => toggleSubmenu("ledger")}>
            <Tooltip title="Customer Ledger (Khata)" placement="right">
              <ListItemIcon><AccountBalanceIcon /></ListItemIcon>
            </Tooltip>
            <ListItemText primary="LEDGER" />
            {showLedgerSubmenu ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={showLedgerSubmenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton component={Link} to="/dashboard/ledger/customer-ledger-view" sx={{ pl: 4 }}>
                <Tooltip title="View Customer Ledger (Khata)" placement="right">
                  <ListItemIcon><VisibilityIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="View Ledger" />
              </ListItemButton>
            </List>
          </Collapse>
         </>
         )}
          {/* Receipt — not currently plan-gated */}
          {showReceiptMenu && (
            <>
          <ListItemButton onClick={() => toggleSubmenu("receipt")}>
            <Tooltip title="Receipt" placement="right">
              <ListItemIcon><ReceiptIcon /></ListItemIcon>
            </Tooltip>
            <ListItemText primary="RECEIPT" />
            {showReceiptSubmenu ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={showReceiptSubmenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton component={Link} to="/dashboard/receipt/generate-receipt" sx={{ pl: 4 }}>
                <Tooltip title="Generate Receipt" placement="right">
                  <ListItemIcon><PrintIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="Generate Receipt" />
              </ListItemButton>
              <ListItemButton component={Link} to="/dashboard/sales/view-receipt" sx={{ pl: 4 }}>
                <Tooltip title="View Receipts" placement="right">
                  <ListItemIcon><VisibilityIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="View Receipts" />
              </ListItemButton>
            </List>
          </Collapse>
            </>
          )}
          {/* Reports — the GROUP is shown as long as "reports" is enabled at
              all (hidden entirely only when the plan has no Reports rights,
              e.g. Trial). Once shown, EACH sub-item is checked individually
              via hasAction("reports", "<key>") and rendered locked (grayed +
              lock icon, not clickable) rather than removed — this is exactly
              your Professional example: Reports menu open, B2B GST row locked. */}
          {showReportsMenu && (
            <>
              <ListItemButton onClick={() => toggleSubmenu("reports")}>
                <Tooltip title="Reports" placement="right">
                  <ListItemIcon><AssessmentIcon /></ListItemIcon>
                </Tooltip>
                <ListItemText primary="REPORTS" />
                {showReportsSubmenu ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={showReportsSubmenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <SubMenuItem
                    to="/dashboard/report/sales-report"
                    label="Sales Register Report"
                    icon={<BarChartIcon />}
                    locked={reportsLocked.salesRegister}
                  />
                  <SubMenuItem
                    to="/dashboard/report/b2b-Sales-report"
                    label="B2B Sales Register(GST)"
                    icon={<BarChartIcon />}
                    locked={reportsLocked.b2bGst}
                  />
                  <SubMenuItem
                    to="/dashboard/report/sales-report-items"
                    label="Sales Report Items"
                    icon={<BarChartIcon />}
                    locked={reportsLocked.salesByItem}
                  />
                  <SubMenuItem
                    to="/dashboard/report/item-report-categoryWise-stock"
                    label="Item Report Stocks"
                    icon={<BarChartIcon />}
                    locked={reportsLocked.categoryStock}
                  />
                </List>
              </Collapse>
            </>
          )}
        </List>
      </ResizableDrawer>

      <Box
        component="main"
        sx={{
          flexGrow:   1,
          padding:    "2px",
          transition: "margin-left 0.3s ease",
          marginLeft: 0,
        }}
      >
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
}
//04/05/2026
// import * as React from "react";
// import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
// import Box from "@mui/material/Box";
// import MuiDrawer from "@mui/material/Drawer";
// import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
// import Toolbar from "@mui/material/Toolbar";
// import List from "@mui/material/List";
// import CssBaseline from "@mui/material/CssBaseline";
// import Typography from "@mui/material/Typography";
// import Divider from "@mui/material/Divider";
// import IconButton from "@mui/material/IconButton";
// import MenuIcon from "@mui/icons-material/Menu";
// import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
// import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// import ListItemButton from "@mui/material/ListItemButton";
// import ListItemIcon from "@mui/material/ListItemIcon";
// import KeyIcon from "@mui/icons-material/Key";
// import ListItemText from "@mui/material/ListItemText";
// import { Link, Outlet, useNavigate } from "react-router-dom";
// import { ExpandLess, ExpandMore } from "@mui/icons-material";
// import Collapse from "@mui/material/Collapse";
// import AccountCircleIcon from "@mui/icons-material/AccountCircle";
// import ExitToAppIcon from "@mui/icons-material/ExitToApp";
// import DashboardIcon from "@mui/icons-material/Dashboard";
// import EditIcon from "@mui/icons-material/Edit";
// import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
// import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
// import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import AssessmentIcon from "@mui/icons-material/Assessment";
// import BarChartIcon from "@mui/icons-material/BarChart";
// import ReceiptIcon from "@mui/icons-material/Receipt";
// import PrintIcon from "@mui/icons-material/Print";
// import InventoryIcon from "@mui/icons-material/Inventory";
// import AssignmentIcon from "@mui/icons-material/Assignment";
// import AddTaskIcon from "@mui/icons-material/AddTask";
// import DataSaverOnSharpIcon from "@mui/icons-material/DataSaverOnSharp";
// import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
// import { Tooltip } from "@mui/material";
// import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
 
// import OrgDetailsPopup       from "../Admin/OrgDetailsPopup";
// import { handleLogout }      from "../Services/AuthService";   // ← FIX: import
 
// const DEFAULT_DRAWER_WIDTH = 240;
 
// // ─── Styled components (unchanged from your original) ────────────────────────
 
// const openedMixin = (theme: Theme, drawerWidth: number): CSSObject => ({
//   width: drawerWidth,
//   transition: theme.transitions.create("width", {
//     easing:   theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.enteringScreen,
//   }),
//   overflowX: "hidden",
// });
 
// const closedMixin = (theme: Theme): CSSObject => ({
//   transition: theme.transitions.create("width", {
//     easing:   theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   overflowX: "hidden",
//   width: `calc(${theme.spacing(7)} + 1px)`,
//   [theme.breakpoints.up("sm")]: {
//     width: `calc(${theme.spacing(8)} + 1px)`,
//   },
// });
 
// const DrawerHeader = styled("div")(({ theme }) => ({
//   display:         "flex",
//   alignItems:      "center",
//   justifyContent:  "flex-end",
//   padding:         theme.spacing(0, 1),
//   ...theme.mixins.toolbar,
// }));
 
// interface AppBarProps extends MuiAppBarProps {
//   open?:        boolean;
//   drawerwidth?: number;
// }
 
// const AppBar = styled(MuiAppBar, {
//   shouldForwardProp: (prop) => prop !== "open" && prop !== "drawerwidth",
// })<AppBarProps>(({ theme, open, drawerwidth = DEFAULT_DRAWER_WIDTH }) => ({
//   zIndex: theme.zIndex.drawer + 1,
//   transition: theme.transitions.create(["width", "margin"], {
//     easing:   theme.transitions.easing.sharp,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   ...(open && {
//     marginLeft: drawerwidth,
//     width:      `calc(100% - ${drawerwidth}px)`,
//     transition: theme.transitions.create(["width", "margin"], {
//       easing:   theme.transitions.easing.sharp,
//       duration: theme.transitions.duration.enteringScreen,
//     }),
//   }),
// }));
 
// const ResizableDrawer = styled(MuiDrawer, {
//   shouldForwardProp: (prop: PropertyKey) =>
//     prop !== "open" && prop !== "drawerWidth",
// })<{ open: boolean; drawerWidth: number }>(({ theme, open, drawerWidth }) => ({
//   width:      drawerWidth,
//   flexShrink: 0,
//   whiteSpace: "nowrap",
//   boxSizing:  "border-box",
//   position:   "relative",
//   ...(open && {
//     ...openedMixin(theme, drawerWidth),
//     "& .MuiDrawer-paper": openedMixin(theme, drawerWidth),
//   }),
//   ...(!open && {
//     ...closedMixin(theme),
//     "& .MuiDrawer-paper": closedMixin(theme),
//   }),
// }));
 
// const ResizeHandle = styled("div")(({ theme }) => ({
//   position:        "absolute",
//   top:             0,
//   right:           0,
//   bottom:          0,
//   width:           "10px",
//   cursor:          "col-resize",
//   zIndex:          theme.zIndex.drawer + 2,
//   "&:hover": {
//     backgroundColor: "rgba(0, 0, 0, 0.1)",
//   },
// }));
 
// // ─── Component ────────────────────────────────────────────────────────────────
 
// export default function MiniDrawer() {
//   const orgName = localStorage.getItem("orgName");
//   const theme   = useTheme();
//   // useNavigate kept in case other nav actions need it; not used for logout anymore
//   const navigate = useNavigate();
 
//   const [open,                  setOpen]                  = React.useState(false);
//   const [showDashboardSubmenu,  setShowDashboardSubmenu]  = React.useState(false);
//   const [showAdminSubmenu,      setShowAdminSubmenu]      = React.useState(false);
//   const [showSalesSubmenu,      setShowSalesSubmenu]      = React.useState(false);
//   const [showReportsSubmenu,    setShowReportsSubmenu]    = React.useState(false);
//   const [showReceiptSubmenu,    setShowReceiptSubmenu]    = React.useState(false);
//   const [showInventorySubmenu,  setShowInventorySubmenu]  = React.useState(false);
//   const [showMasterSubmenu,     setShowMasterSubmenu]     = React.useState(false);
//   const [showPurchaseSubmenu,   setShowPurchaseSubmenu]   = React.useState(false);
//   const [showLedgerSubmenu,     setShowLedgerSubmenu]     = React.useState(false);
//   const [drawerWidth,           setDrawerWidth]           = React.useState(DEFAULT_DRAWER_WIDTH);
//   const [isPopupVisible,        setPopupVisible]          = React.useState(false);
//   const [isResizing,            setIsResizing]            = React.useState(false);
 
//   const handleDrawerOpen  = () => setOpen(true);
//   const handleDrawerClose = () => setOpen(false);
//   const openPopup         = () => setPopupVisible(true);
//   const closePopup        = () => setPopupVisible(false);
 
//   const toggleSubmenu = (menu: string) => {
//     setShowDashboardSubmenu(menu === "dashboard" ? !showDashboardSubmenu : false);
//     setShowAdminSubmenu(    menu === "admin"      ? !showAdminSubmenu     : false);
//     setShowMasterSubmenu(   menu === "master"     ? !showMasterSubmenu    : false);
//     setShowSalesSubmenu(    menu === "sales"      ? !showSalesSubmenu     : false);
//     setShowReportsSubmenu(  menu === "reports"    ? !showReportsSubmenu   : false);
//     setShowReceiptSubmenu(  menu === "receipt"    ? !showReceiptSubmenu   : false);
//     setShowPurchaseSubmenu( menu === "purchase"   ? !showPurchaseSubmenu  : false);
//     setShowInventorySubmenu(menu === "inventory"  ? !showInventorySubmenu : false);
//     setShowLedgerSubmenu(   menu === "ledger"     ? !showLedgerSubmenu    : false);
//   };
 
//   // ── Drawer resize ─────────────────────────────────────────────────────────
//   const startResizing = React.useCallback(() => setIsResizing(true),  []);
//   const stopResizing  = React.useCallback(() => setIsResizing(false), []);
 
//   const resize = React.useCallback(
//     (e: MouseEvent) => {
//       if (isResizing) {
//         setDrawerWidth(Math.min(Math.max(e.clientX, 200), 500));
//       }
//     },
//     [isResizing]
//   );
 
//   React.useEffect(() => {
//     window.addEventListener("mousemove", resize);
//     window.addEventListener("mouseup",   stopResizing);
//     return () => {
//       window.removeEventListener("mousemove", resize);
//       window.removeEventListener("mouseup",   stopResizing);
//     };
//   }, [resize, stopResizing]);
 
//   // ─── Render ───────────────────────────────────────────────────────────────
//   return (
//     <Box sx={{ display: "flex" }}>
//       <CssBaseline />
 
//       <AppBar
//         sx={{ backgroundColor: "#663399" }}
//         position="fixed"
//         open={open}
//         drawerwidth={drawerWidth}
//       >
//         <Toolbar>
//           <IconButton
//             color="inherit"
//             aria-label="open drawer"
//             onClick={handleDrawerOpen}
//             edge="start"
//             sx={{ marginRight: 5, ...(open && { display: "none" }) }}
//           >
//             <MenuIcon />
//           </IconButton>
 
//           <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
//             {orgName}
//           </Typography>
 
//           <IconButton color="inherit" aria-label="org profile" onClick={openPopup}>
//             <AccountCircleIcon />
//           </IconButton>
//           {isPopupVisible && <OrgDetailsPopup onClose={closePopup} />}
 
//           {/* ── FIX: use handleLogout from AuthService ── */}
//           <Tooltip title="Sign out">
//             <IconButton
//               color="inherit"
//               aria-label="sign out"
//               onClick={handleLogout}   // ← calls POST /api/auth/logout then clears storage
//             >
//               <ExitToAppIcon />
//             </IconButton>
//           </Tooltip>
//         </Toolbar>
//       </AppBar>
 
//       <ResizableDrawer
//         variant="permanent"
//         open={open}
//         drawerWidth={drawerWidth}
//         sx={{
//           "& .MuiDrawer-paper": {
//             backgroundColor: "#f0f8ff",
//             color: "#000",
//           },
//         }}
//       >
//         <ResizeHandle onMouseDown={startResizing} />
//         <DrawerHeader>
//           <IconButton onClick={handleDrawerClose}>
//             {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
//           </IconButton>
//         </DrawerHeader>
//         <Divider />
 
//         <List
//           sx={{
//             "& .MuiListItemText-primary": {
//               fontWeight:        "bold",
//               fontFamily:        '"Poppins", sans-serif',
//               fontSize:          "0.9rem",
//               color:             "#556b2f",
//               whiteSpace:        "normal",
//               overflow:          "hidden",
//               textOverflow:      "ellipsis",
//               display:           "-webkit-box",
//               WebkitLineClamp:   2,
//               WebkitBoxOrient:   "vertical",
//             },
//             "& .MuiListItemButton-root": { py: 1.5 },
//           }}
//         >
//           {/* Dashboard */}
//           <ListItemButton component={Link} to="/dashboard">
//             <Tooltip title="Dashboard" placement="right">
//               <ListItemIcon><DashboardIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="DASHBOARD" />
//           </ListItemButton>
 
//           {/* Master */}
//           <ListItemButton onClick={() => toggleSubmenu("master")}>
//             <Tooltip title="Master" placement="right">
//               <ListItemIcon><AddTaskIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="MASTER" />
//             {showMasterSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showMasterSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               {[
//                 { to: "Master/inventory/add-category",     label: "Category Master" },
//                 { to: "Master/inventory/add-brand",        label: "Brand Master" },
//                 { to: "Master/inventory/add-manufacturer", label: "Manufacturer Master" },
//                 { to: "Master/inventory/add-warehouse",    label: "Warehouse Master" },
//               ].map(({ to, label }) => (
//                 <ListItemButton key={to} component={Link} to={to} sx={{ pl: 4 }}>
//                   <Tooltip title={label} placement="right">
//                     <ListItemIcon><DataSaverOnSharpIcon /></ListItemIcon>
//                   </Tooltip>
//                   <ListItemText primary={label} />
//                 </ListItemButton>
//               ))}
//             </List>
//           </Collapse>
 
//           {/* Admin */}
//           <ListItemButton onClick={() => toggleSubmenu("admin")}>
//             <Tooltip title="Admin" placement="right">
//               <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="ADMIN" />
//             {showAdminSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showAdminSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               <ListItemButton component={Link} to="/dashboard/admin/edit-org" sx={{ pl: 4 }}>
//                 <Tooltip title="Edit Org Profile" placement="right">
//                   <ListItemIcon><EditIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="Edit Org Profile" />
//               </ListItemButton>
//               <ListItemButton component={Link} to="Admin/view-user" sx={{ pl: 4 }}>
//                 <Tooltip title="View Users" placement="right">
//                   <ListItemIcon><VisibilityIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="View Users" />
//               </ListItemButton>
//               <ListItemButton component={Link} to="Admin/change-password" sx={{ pl: 4 }}>
//                 <Tooltip title="Change Password" placement="right">
//                   <ListItemIcon><KeyIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="Change Password" />
//               </ListItemButton>
//             </List>
//           </Collapse>
 
//           {/* Purchase */}
//           <ListItemButton onClick={() => toggleSubmenu("purchase")}>
//             <Tooltip title="Purchase" placement="right">
//               <ListItemIcon><AssignmentIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="PURCHASE" />
//             {showPurchaseSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showPurchaseSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               <ListItemButton component={Link} to="/dashboard/purchase/purchase-order-form" sx={{ pl: 4 }}>
//                 <Tooltip title="Create Purchase Order" placement="right">
//                   <ListItemIcon><AddTaskIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="Create PO" />
//               </ListItemButton>
//               <ListItemButton component={Link} to="/dashboard/purchase/view-purchase-orders" sx={{ pl: 4 }}>
//                 <Tooltip title="View Purchase Order" placement="right">
//                   <ListItemIcon><VisibilityIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="View PO" />
//               </ListItemButton>
//             </List>
//           </Collapse>
 
//           {/* Inventory */}
//           <ListItemButton onClick={() => toggleSubmenu("inventory")}>
//             <Tooltip title="Inventory" placement="right">
//               <ListItemIcon><InventoryIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="INVENTORY" />
//             {showInventorySubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showInventorySubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               <ListItemButton component={Link} to="/dashboard/inventory/add-new-item" sx={{ pl: 4 }}>
//                 <Tooltip title="Add Inventory" placement="right">
//                   <ListItemIcon><AddCircleOutlineIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="Add Inventory" />
//               </ListItemButton>
//               <ListItemButton component={Link} to="/dashboard/inventory/view-inventory" sx={{ pl: 4 }}>
//                 <Tooltip title="View Inventory" placement="right">
//                   <ListItemIcon><VisibilityIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="View Inventory" />
//               </ListItemButton>
//               <ListItemButton component={Link} to="/dashboard/inventory/stock-management" sx={{ pl: 4 }}>
//                 <Tooltip title="Stock Management" placement="right">
//                   <ListItemIcon><InventoryIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="Stock Management" />
//               </ListItemButton>
//             </List>
//           </Collapse>
 
//           {/* Sales */}
//           <ListItemButton onClick={() => toggleSubmenu("sales")}>
//             <Tooltip title="Sales" placement="right">
//               <ListItemIcon><MonetizationOnIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="SALES" />
//             {showSalesSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showSalesSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               {[
//                 { to: "/dashboard/sales/new-invoice",          label: "New Invoice",      icon: <AddCircleOutlineIcon /> },
//                 { to: "/dashboard/sales/rapid-Quick-invoice",  label: "GST Invoice",      icon: <AssignmentIcon /> },
//                 { to: "/dashboard/sales/quick-invoice",        label: "Quick Invoice",    icon: <AssignmentIcon /> },
//                 { to: "/dashboard/sales/performa-invoice",     label: "Performa Invoice", icon: <AssignmentIcon /> },
//                 { to: "/dashboard/sales/credit-note",         label: "Credit Note",       icon: <AddCircleOutlineIcon color="success" /> },
//                 { to: "/dashboard/sales/debit-note",         label: "Debit Note",         icon:<RemoveCircleOutlineIcon color="error" />},
//                  { to: "/dashboard/sales/view-invoice",         label: "View Invoice",     icon: <VisibilityIcon /> },
//               ].map(({ to, label, icon }) => (
//                 <ListItemButton key={to} component={Link} to={to} sx={{ pl: 4 }}>
//                   <Tooltip title={label} placement="right">
//                     <ListItemIcon>{icon}</ListItemIcon>
//                   </Tooltip>
//                   <ListItemText primary={label} />
//                 </ListItemButton>
//               ))}
//             </List>
//           </Collapse>
 
//           {/* Ledger */}
//           <ListItemButton onClick={() => toggleSubmenu("ledger")}>
//             <Tooltip title="Customer Ledger (Khata)" placement="right">
//               <ListItemIcon><AccountBalanceIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="LEDGER" />
//             {showLedgerSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showLedgerSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               <ListItemButton component={Link} to="/dashboard/ledger/customer-ledger-view" sx={{ pl: 4 }}>
//                 <Tooltip title="View Customer Ledger (Khata)" placement="right">
//                   <ListItemIcon><VisibilityIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="View Ledger" />
//               </ListItemButton>
//             </List>
//           </Collapse>
 
//           {/* Receipt */}
//           <ListItemButton onClick={() => toggleSubmenu("receipt")}>
//             <Tooltip title="Receipt" placement="right">
//               <ListItemIcon><ReceiptIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="RECEIPT" />
//             {showReceiptSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showReceiptSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               <ListItemButton component={Link} to="/dashboard/receipt/generate-receipt" sx={{ pl: 4 }}>
//                 <Tooltip title="Generate Receipt" placement="right">
//                   <ListItemIcon><PrintIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="Generate Receipt" />
//               </ListItemButton>
//               <ListItemButton component={Link} to="/dashboard/sales/view-receipt" sx={{ pl: 4 }}>
//                 <Tooltip title="View Receipts" placement="right">
//                   <ListItemIcon><VisibilityIcon /></ListItemIcon>
//                 </Tooltip>
//                 <ListItemText primary="View Receipts" />
//               </ListItemButton>
//             </List>
//           </Collapse>
 
//           {/* Reports */}
//           <ListItemButton onClick={() => toggleSubmenu("reports")}>
//             <Tooltip title="Reports" placement="right">
//               <ListItemIcon><AssessmentIcon /></ListItemIcon>
//             </Tooltip>
//             <ListItemText primary="REPORTS" />
//             {showReportsSubmenu ? <ExpandLess /> : <ExpandMore />}
//           </ListItemButton>
//           <Collapse in={showReportsSubmenu} timeout="auto" unmountOnExit>
//             <List component="div" disablePadding>
//               {[
//                 { to: "/dashboard/report/sales-report",                      label: "Sales Register Report" },
//                 { to: "/dashboard/report/b2b-Sales-report",                  label: "B2B Sales Register(GST)"},
//                 { to: "/dashboard/report/sales-report-items",                label: "Sales Report Items" },
//                 { to: "/dashboard/report/item-report-categoryWise-stock",    label: "Item Report Stocks" },
//               ].map(({ to, label }) => (
//                 <ListItemButton key={to} component={Link} to={to} sx={{ pl: 4 }}>
//                   <Tooltip title={label} placement="right">
//                     <ListItemIcon><BarChartIcon /></ListItemIcon>
//                   </Tooltip>
//                   <ListItemText primary={label} />
//                 </ListItemButton>
//               ))}
//             </List>
//           </Collapse>
//         </List>
//       </ResizableDrawer>
 
//       <Box
//         component="main"
//         sx={{
//           flexGrow:   1,
//           padding:    "2px",
//           transition: "margin-left 0.3s ease",
//           marginLeft: 0,
//         }}
//       >
//         <DrawerHeader />
//         <Outlet />
//       </Box>
//     </Box>
//   );
// }
